import { CheckoutStep } from './checkout-step';
import { useCustomer } from '../../hooks/use-customer-context';
import { StepAction } from './checkout-view';
import { SelectPayment } from '../payment/select-payment/select-payment';
import { Payment, PaymentMethod, PaymentMethodType, StripeCard } from '../../clients/quipli-service/payment/payment';
import Icon from '../../components/icons/icon';
import { Typography } from '../../components/typography/typography';
import { UserAddress } from '../../clients/quipli-service/user/user';
import { PaymentIntent } from '@stripe/stripe-js';

export const CheckoutPaymentSummary = ({
  step,
  currentStep,
  isSubmitting,
  amount,
  setCurrentStep,
  paymentMethod,
  setPaymentMethod,
  address,
  setPaymentIntent,
  setPayment,
}: {
  step: number;
  currentStep: StepAction;
  isSubmitting: boolean;
  amount: number;
  paymentMethod?: PaymentMethod;
  setCurrentStep: (step: StepAction) => void;
  setPaymentMethod: (paymentMethod: PaymentMethod) => void;
  address?: UserAddress;
  setPaymentIntent?: (paymentIntent: PaymentIntent) => void;
  setPayment?: (payment: Payment) => void;
}) => {
  const customer = useCustomer();
  const isCompleted = currentStep > StepAction.Payment;

  const onConfirmPayment = (paymentMethod: PaymentMethod) => {
    setPaymentMethod(paymentMethod);
    setCurrentStep(StepAction.DriversLicense);
  };

  const stripeCardIcon = (card?: StripeCard) => {
    if (card === undefined) {
      return undefined;
    }

    switch (card.brand.toLowerCase().trim()) {
      case 'visa':
        return <Icon icon={'VisaCardLogo'} size={'medium'}></Icon>;
      case 'mastercard':
        return <Icon icon={'MasterCardLogo'} size={'medium'}></Icon>;
      case 'amex':
      case 'americanexpress':
        return <Icon icon={'AmericanExpressLogo'} size={'medium'}></Icon>;
    }

    return undefined;
  };

  return (
    <CheckoutStep
      title="Payment"
      step={step}
      isCompleted={isCompleted}
      contentPadding="p-3"
      isEditDisabled={isSubmitting}
      onEdit={() => setCurrentStep(StepAction.Payment)}
    >
      {currentStep >= StepAction.Payment && customer && (
        <>
          {!isCompleted ? (
            <SelectPayment
              amount={amount}
              onConfirmPayment={onConfirmPayment}
              address={address}
              setPaymentIntent={setPaymentIntent}
              setPayment={setPayment}
              mode={'payment'}
            />
          ) : (
            <div>
              {paymentMethod?.type === PaymentMethodType.Stripe &&
                (paymentMethod.method && !paymentMethod.card ? (
                  <Typography type="text-sm" weight="bold" className="text-grey-500">
                    Affirm
                  </Typography>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2 items-center">
                      {paymentMethod?.card?.nickname && paymentMethod?.card?.nickname.length > 0 && (
                        <Typography type="text-sm" weight="bold" className="text-grey-500">
                          {paymentMethod?.card?.nickname}
                        </Typography>
                      )}
                      {stripeCardIcon(paymentMethod?.card)}
                      <Typography type="text-sm" className="text-grey-500">
                        •••• {paymentMethod?.card?.last4}
                      </Typography>
                    </div>

                    <Typography type="text-sm" className="text-grey-500">
                      {paymentMethod?.card?.cardholderName}
                    </Typography>
                  </div>
                ))}

              {paymentMethod?.type === PaymentMethodType.OnAccount && (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Typography type="text-sm" weight="bold" className="text-grey-500">
                      Account
                    </Typography>
                    <Typography type="text-sm" className="text-grey-600">
                      {paymentMethod.account.accountNumber}
                    </Typography>
                  </div>

                  {paymentMethod.poNumber && (
                    <div className="flex gap-2">
                      <Typography type="text-sm" weight="bold" className="text-grey-500">
                        PO Number
                      </Typography>
                      <Typography type="text-sm" className="text-grey-600">
                        {paymentMethod.poNumber}
                      </Typography>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </CheckoutStep>
  );
};
